import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import {
  AttributeCount,
  CountedItem,
  EMPTY_COMPETITOR_ATTRIBUTES,
  findProductBrand,
  getAvgPositionWithRankedShare,
  getMentionedBrands,
  getShareOfVoice,
  getVisibilityRank,
  getVisibilityScore,
  groupBy,
  normalizeAttributeCasing,
  normalizeBrand,
  rankByFrequency
} from './alexa-visibility-utils';

const POSITIONS = 3;

export interface PositionedProduct {
  label: string;
  // The brand the product belongs to, when one of the brands Alexa mentioned prefixes its name.
  brand?: string;
  // How many results recommend it in their top 3, and the share of all results that is.
  count: number;
  percent: number;
  // How many of those results put it 1st, 2nd and 3rd.
  positions: number[];
}

/**
 * Every product Alexa put in a response's top 3, most recommended first, with how often it took
 * each of the three positions. The share is of ALL results given (not of recommendations), so
 * "364/2297 (16%)" reads as "recommended in 364 of 2,297 responses".
 */
export const getProductPositionStats = (results: AlexaVisibilityResult[]): PositionedProduct[] => {
  const brands = getMentionedBrands(results);
  const byProduct = new Map<string, PositionedProduct>();

  results.forEach(result => {
    const seen = new Set<string>();
    (result.classification?.top3Recommendations ?? []).slice(0, POSITIONS).forEach((raw, index) => {
      const key = normalizeBrand(raw);
      if (!key || seen.has(key)) return;
      seen.add(key);
      const entry = byProduct.get(key) ?? {
        label: raw.trim(),
        brand: findProductBrand(raw, brands),
        count: 0,
        percent: 0,
        positions: Array.from({ length: POSITIONS }, () => 0)
      };
      entry.count += 1;
      entry.positions[index] += 1;
      byProduct.set(key, entry);
    });
  });

  return Array.from(byProduct.values())
    .map(entry => ({
      ...entry,
      percent: results.length > 0 ? (entry.count / results.length) * 100 : 0
    }))
    .sort((a, b) => b.count - a.count);
};

export interface TargetCitationSummary {
  total: number;
  cited: number;
  rank1: number;
  rank2: number;
  rank3: number;
  // Every result where the brand did not land in the top 3, including ones where it was cited
  // further down or not at all.
  notRanked: number;
}

/** How often the target brand was cited and where it ranked, out of all the results given. */
export const getTargetCitationSummary = (
  results: AlexaVisibilityResult[]
): TargetCitationSummary => {
  const countRank = (rank: number): number =>
    results.filter(result => result.classification?.targetBrandRank === rank).length;
  const rank1 = countRank(1);
  const rank2 = countRank(2);
  const rank3 = countRank(3);
  return {
    total: results.length,
    cited: results.filter(result => result.classification?.targetBrandMentioned).length,
    rank1,
    rank2,
    rank3,
    notRanked: results.length - rank1 - rank2 - rank3
  };
};

export interface QueryCitationStats {
  query: string;
  results: AlexaVisibilityResult[];
  // Distinct runs the query appears in.
  runs: number;
  visibilityScore: number;
  shareOfVoice: number;
  typicalRank: number | undefined;
  avgPosition: number | undefined;
  // True when Alexa's top 3 was not the same in every run.
  variesAcrossRuns: boolean;
}

const top3Key = (result: AlexaVisibilityResult): string =>
  (result.classification?.top3Recommendations ?? []).map(normalizeBrand).join('|');

/** One query's numbers across all the results (runs) given for it. */
export const getQueryCitationStats = (
  query: string,
  results: AlexaVisibilityResult[],
  targetBrand: string
): QueryCitationStats => ({
  query,
  results,
  runs: new Set(results.map(result => result.runId)).size,
  visibilityScore: getVisibilityScore(results),
  shareOfVoice: getShareOfVoice(results, targetBrand),
  typicalRank: getVisibilityRank(results),
  avgPosition: getAvgPositionWithRankedShare(results).avgPosition,
  variesAcrossRuns: new Set(results.filter(top3Key).map(top3Key)).size > 1
});

/** Stats for every query in the results, A to Z. */
export const getQueryCitationStatsList = (
  results: AlexaVisibilityResult[],
  targetBrand: string
): QueryCitationStats[] =>
  Array.from(groupBy(results, result => result.query).entries())
    .map(([query, queryResults]) => getQueryCitationStats(query, queryResults, targetBrand))
    .sort((a, b) => a.query.localeCompare(b.query));

/**
 * The queries the brand does best on: highest visibility score first, then the better (lower)
 * average position, then the query with more runs behind it.
 */
export const rankQueriesByVisibility = (
  stats: QueryCitationStats[],
  limit = POSITIONS
): QueryCitationStats[] =>
  [...stats]
    .sort(
      (a, b) =>
        b.visibilityScore - a.visibilityScore ||
        (a.avgPosition ?? Infinity) - (b.avgPosition ?? Infinity) ||
        b.runs - a.runs ||
        a.query.localeCompare(b.query)
    )
    .slice(0, limit);

export interface MissingAttribute {
  attribute: string;
  // A competitor Alexa cited it for.
  brand: string;
  count: number;
}

/** Attributes cited for competitors in these results but never for the target brand. */
export const getMissingAttributes = (results: AlexaVisibilityResult[]): MissingAttribute[] => {
  const cited = new Set(
    results.flatMap(result => result.classification?.keyAttributesCited ?? []).map(normalizeBrand)
  );
  const missing = new Map<string, MissingAttribute>();
  results.forEach(result => {
    Object.entries(
      result.classification?.competitorAttributes ?? EMPTY_COMPETITOR_ATTRIBUTES
    ).forEach(([brand, attributes]) => {
      attributes.forEach(attribute => {
        const key = normalizeBrand(attribute);
        if (!key || cited.has(key)) return;
        const entry = missing.get(key) ?? { attribute: attribute.trim(), brand, count: 0 };
        entry.count += 1;
        missing.set(key, entry);
      });
    });
  });
  return Array.from(missing.values()).sort((a, b) => b.count - a.count);
};

/** The attributes cited for the target brand across these results, most cited first. */
export const getTargetAttributes = (results: AlexaVisibilityResult[]): AttributeCount[] =>
  normalizeAttributeCasing(
    results.flatMap(result => result.classification?.keyAttributesCited ?? [])
  );

/** The follow-up questions Alexa suggested, most repeated first (casing ignored). */
export const getFollowUpQuestions = (results: AlexaVisibilityResult[]): CountedItem[] =>
  rankByFrequency(results.flatMap(result => result.followupQuestions ?? []));

/** One row per follow-up question (not just the top ones), for the Excel export. */
export const buildFollowUpExportRows = (questions: CountedItem[]): Record<string, unknown>[] =>
  questions.map(({ label, count }, index) => ({
    Rank: index + 1,
    Question: label,
    'Times Asked': count
  }));

/** The excerpt from the most recent result that has one. */
export const getLatestExcerpt = (results: AlexaVisibilityResult[]): string | undefined => {
  const latest = [...results]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .find(result => result.classification?.rawExcerpt ?? result.response);
  return latest?.classification?.rawExcerpt ?? latest?.response;
};

/** Every response format in the results, A to Z, for the response-type filter. */
export const getResponseFormats = (results: AlexaVisibilityResult[]): string[] =>
  Array.from(
    new Set(
      results
        .map(result => result.classification?.responseFormat?.trim() ?? '')
        .filter(format => format !== '')
    )
  ).sort((a, b) => a.localeCompare(b));
